import {
  useLazyQuery as useApolloLazyQuery,
  useMutation as useApolloMutation,
  useQuery as useApolloQuery,
} from '@apollo/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';
import {
  AspirationTodayResponse,
  completedAspirationsFbdOptions,
  completedAspirationsOptions,
  CreateAspPoolRequest,
  EmbryologistDataEntryRequest,
  fertDataOptions,
  poolOptions,
  PoolResponse,
  qcDataOptions,
  QcDataResponse,
  QcDataUpdateRequest,
  RemoveAspPoolRequest,
  showStopSignOptions,
  useCreateAspPoolMutation,
  useRefreshStopGoSignMutation,
  useRemoveAspPoolMutation,
  useUpdateAspirationDataEntryMutation,
  useUpdateQcDataMutation,
} from '@/services/opu-search';
import {
  CREATE_NEW_ASP_POOL,
  GET_ASP_DATA_POOL,
  GET_COMPLETED_ASPIRATIONS_TODAY,
  GET_COMPLETED_ASPIRATIONS_TODAY_FBD,
  GET_EMB_FERT_DATA_BY_DONOR_ID,
  GET_TODAYS_QC_DATA_OPUSEARCH,
  REFRESH_STOP_GO_SIGN,
  REMOVE_ASP_POOL,
  SAVE_QC_DATA_OPUSEARCH,
  SHOW_STOP_SIGN,
  UPDATE_ASPIRATION_OPU_SEARCH,
} from '@/types.gql';

const FLAG = '29386_opu_search_graphql_to_rest';

export function useCompletedAspirationsQuery(params: {
  date: string;
  siteId: string | null;
  isFarbro: boolean;
  location?: string | null;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.siteId;

  const restRegularQuery = useQuery({
    ...completedAspirationsOptions(token, {
      date: params.date,
      siteId: params.siteId!,
    }),
    enabled: !!token && isRest && enabled && !params.isFarbro,
    refetchInterval: 10_000,
  });

  const restFbdQuery = useQuery({
    ...completedAspirationsFbdOptions(token, {
      date: params.date,
      siteId: params.siteId!,
      location: params.location ?? '',
    }),
    enabled: !!token && isRest && enabled && params.isFarbro,
    refetchInterval: 10_000,
  });

  const [runRegular, regular] = useApolloLazyQuery(
    GET_COMPLETED_ASPIRATIONS_TODAY,
    {
      fetchPolicy: 'network-only',
      pollInterval: 10_000,
    },
  );

  const [runFbd, fbd] = useApolloLazyQuery(
    GET_COMPLETED_ASPIRATIONS_TODAY_FBD,
    {
      fetchPolicy: 'network-only',
      pollInterval: 10_000,
    },
  );

  if (isRest) {
    const restData = params.isFarbro
      ? restFbdQuery.data
      : restRegularQuery.data;
    const restRefetch = params.isFarbro
      ? restFbdQuery.refetch
      : restRegularQuery.refetch;
    return {
      data: (restData ?? []) as AspirationTodayResponse[],
      refetch: () => {
        void restRefetch();
      },
      runRegular: undefined as unknown as typeof runRegular,
      runFbd: undefined as unknown as typeof runFbd,
    };
  }

  const gqlData = params.isFarbro
    ? fbd.data?.todaysCompletedAspirationsFBD
    : regular.data?.todaysCompletedAspirationsNew;
  const gqlRefetch = params.isFarbro ? fbd.refetch : regular.refetch;

  return {
    data: (gqlData ?? []) as AspirationTodayResponse[],
    refetch: () => {
      void gqlRefetch();
    },
    runRegular,
    runFbd,
  };
}

export function useFertDataQuery(params: {
  donorId: string | null;
  ranch?: string | null;
  enabled?: boolean;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const canFetch = (params.enabled ?? true) && !!params.donorId;

  const restQuery = useQuery({
    ...fertDataOptions(token, {
      donorId: params.donorId!,
      ranch: params.ranch,
    }),
    enabled: !!token && isRest && canFetch,
  });

  const gqlQuery = useApolloQuery(GET_EMB_FERT_DATA_BY_DONOR_ID, {
    skip: isRest || !canFetch,
    variables: { donorId: params.donorId ?? '', ranch: params.ranch },
  });

  if (isRest) {
    return {
      data: restQuery.data
        ? { embFertDataByDonorId: restQuery.data }
        : undefined,
    };
  }
  return {
    data: gqlQuery.data,
  };
}

export function useQcDataQuery(params: {
  date: string;
  siteId: string | null;
  location?: string | null;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.siteId;

  const restQuery = useQuery({
    ...qcDataOptions(token, {
      date: params.date,
      siteId: params.siteId!,
      location: params.location,
    }),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_TODAYS_QC_DATA_OPUSEARCH, {
    variables: {
      siteId: params.siteId as string,
      date: params.date,
      location: params.location,
    },
    fetchPolicy: 'network-only',
    skip: isRest,
  });

  if (isRest) {
    return {
      data: restQuery.data as QcDataResponse | undefined,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.todaysQcDataOpuSearch as QcDataResponse | undefined,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useShowStopSignQuery(params: { siteId: string | null }) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.siteId;

  const restQuery = useQuery({
    ...showStopSignOptions(token, params.siteId!),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useApolloQuery(SHOW_STOP_SIGN, {
    variables: { siteId: params.siteId as string },
    fetchPolicy: 'network-only',
    skip: isRest || !enabled,
  });

  if (isRest) {
    return {
      data: restQuery.data,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.showStopSign as boolean | undefined,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useRefreshStopGoSign() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useRefreshStopGoSignMutation();
  const [gqlRefresh] = useApolloLazyQuery(REFRESH_STOP_GO_SIGN);

  const mutateAsync = async (params: { siteId: string; val: string }) => {
    if (isRest) {
      return restMutation.mutateAsync(params);
    }
    return gqlRefresh({
      variables: { val: params.val, siteId: params.siteId },
    });
  };

  return { mutateAsync };
}

export function useUpdateAspirationOpuSearch() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateAspirationDataEntryMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(
    UPDATE_ASPIRATION_OPU_SEARCH,
  );

  const mutateAsync = async (params: {
    input: EmbryologistDataEntryRequest;
    isReturn: string;
  }) => {
    if (isRest) {
      return restMutation.mutateAsync(params);
    }
    return gqlMutate({
      variables: { input: params.input, isReturn: params.isReturn },
    });
  };

  return {
    mutateAsync,
    loading: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useAspDataPoolQuery(params: {
  aspirationDate: string | null;
  siteId: string | null;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.aspirationDate && !!params.siteId;

  const restQuery = useQuery({
    ...poolOptions(token, {
      aspirationDate: params.aspirationDate!,
      siteId: params.siteId!,
    }),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_ASP_DATA_POOL, {
    variables: {
      siteId: params.siteId as string,
      aspirationDate: params.aspirationDate as string,
    },
    fetchPolicy: 'network-only',
    skip: isRest || !enabled,
  });

  if (isRest) {
    return {
      data: restQuery.data as PoolResponse[] | undefined,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.aspDataPool as PoolResponse[] | undefined,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useCreateAspPool() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useCreateAspPoolMutation();
  const [gqlMutate] = useApolloMutation(CREATE_NEW_ASP_POOL);

  const mutateAsync = async (request: CreateAspPoolRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    const result = await gqlMutate({
      variables: {
        aspirationIds: request.aspirationIds,
        siteId: request.siteId,
      },
    });
    return result.data?.createNewAspPool as string;
  };

  return { mutateAsync };
}

export function useRemoveAspPool() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useRemoveAspPoolMutation();
  const [gqlMutate] = useApolloMutation(REMOVE_ASP_POOL);

  const mutateAsync = async (request: RemoveAspPoolRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    await gqlMutate({
      variables: { aspirationPoolIds: request.aspirationPoolIds },
    });
    return true;
  };

  return { mutateAsync };
}

export function useSaveQcData() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateQcDataMutation();
  const queryClient = useQueryClient();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(
    SAVE_QC_DATA_OPUSEARCH,
  );

  const mutateAsync = async (params: {
    input: QcDataUpdateRequest;
    aspirationDate: string;
    siteId: string;
    location?: string | null;
  }) => {
    if (isRest) {
      const result = await restMutation.mutateAsync(params);
      await queryClient.invalidateQueries({
        queryKey: ['opu-search', 'qc'],
      });
      return result;
    }
    return gqlMutate({
      variables: {
        input: params.input,
        aspirationDate: params.aspirationDate,
        siteId: params.siteId,
        location: params.location,
      },
    });
  };

  return {
    mutateAsync,
    loading: isRest ? restMutation.isPending : gqlLoading,
  };
}
